import { d as debounce } from './index-DfRgBxEx.js';
import { f as fetchConfig } from './index-DoPtEJTE.js';

/* Update cart items */
if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();

      this.lineItemStatusElement =
        document.getElementById('shopping-cart-line-item-status') ||
        document.getElementById('CartDrawer-LineItemStatus');

      const debouncedOnChange = debounce((event) => {
        if (event.target != document.getElementById('Cart-note')) {
          this.onChange(event);
        }
      }, 300);

      this.addEventListener('change', debouncedOnChange.bind(this));

      // Listen for cart updates in other components
      document.addEventListener('cart-updated', (event) => {
        if (event.detail) {
          this.enableLoading();
          this.renderContents(event.detail);
          this.disableLoading();
        }
      });
    }

    onChange(event) {
      this.updateQuantity(
        event.target.dataset.index,
        event.target.value,
        document.activeElement.getAttribute('name'),
        event
      );
    }

    /**
     * Get all cart sections that need to be re-rendered on update
     * @returns {Array} Array of section objects with id, section name, and selector
     */
    getSectionsToRender() {
      const sections = [];

      // Main cart items section
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems?.dataset?.id) {
        sections.push({
          id: 'main-cart-items',
          section: mainCartItems.dataset.id,
          selector: '.js-cart-item-contents',
        });
      }

      // Cart totals section
      const mainCartTotals = document.getElementById('main-cart-totals');
      if (mainCartTotals?.dataset?.id) {
        sections.push({
          id: 'main-cart-totals',
          section: mainCartTotals.dataset.id,
          selector: '.js-cart-footer-contents',
        });
      }

      // Cart icon bubble is always included
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '[data-cart-icon-bubble]',
      });

      return sections;
    }

    /**
     * Update the quantity of a cart item
     * @param {string} line - The line item index
     * @param {number} quantity - The new quantity
     * @param {string} name - The name of the active element
     * @param {Event} event - The triggering event
     */
    async updateQuantity(line, quantity, name, event) {
      this.enableLoading();

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      try {
        const response = await fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body,
        });

        const parsedState = await response.json();

        // Search for elements in the DOM
        const items = document.querySelectorAll('.cart-item');
        let quantityElement = document.getElementById(`Quantity-${line}`);

        // Check if there are errors in the response
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // Tell the user that something is wrong when there are no sections returned in the response.
        if (!parsedState.sections) {
          event.target.value = event.target.defaultValue;
          throw new Error(window.cartStrings.error);
        }

        // Update cart contents
        this.renderContents(parsedState);

        // Dispatch cart-updated event for other components
        document.dispatchEvent(
          new CustomEvent('cart-updated', {
            bubbles: true,
            detail: parsedState,
          })
        );
      } catch (error) {
        // Show the general error message that something went wrong to the users
        const errorMessageTarget = document.getElementById('cart-errors');
        errorMessageTarget.textContent = window.cartStrings.error;
      } finally {
        this.disableLoading();
      }
    }

    /**
     * Renders the cart contents based on the provided state
     * Updates cart items, footer, and cart count displays
     * @param {Object} parsedState - The cart state containing items and sections
     */
    renderContents(parsedState) {
      if (!parsedState || !parsedState.sections) {
        return;
      }

      this.getSectionsToRender().forEach((section) => {
        if (!parsedState.sections[section.section]) {
          return;
        }

        const elementToReplace = document.getElementById(section.id);
        if (!elementToReplace) return;

        const selectorElement = section.selector
          ? elementToReplace.querySelector(section.selector)
          : elementToReplace;

        if (!selectorElement) return;

        selectorElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });

      // Update all cart count displays
      if (typeof parsedState.item_count === 'number') {
        this.updateAllCartCounts(parsedState.item_count);
      }

      this.toggleEmptyCart();
    }

    /**
     * Update all cart count displays throughout the page
     * @param {number} count - The new cart count
     */
    updateAllCartCounts(count) {
      // Update cart-item-count custom elements
      const cartItemCounts = document.querySelectorAll('cart-item-count');
      cartItemCounts.forEach((counter) => {
        counter.textContent = count;
        counter.classList.toggle('hidden', count === 0);
      });

      // Update cart page count
      const cartPageCounts = document.querySelectorAll('[data-cart-count]');
      cartPageCounts.forEach((counter) => {
        counter.textContent = count;
        counter.classList.toggle('hidden', count === 0);
      });

      // Update cart icon bubble count
      const cartIconBubbleCounts = document.querySelectorAll('.cart-icon-bubble__count');
      cartIconBubbleCounts.forEach((counter) => {
        counter.classList.toggle('hidden', count === 0);
        const cartCount = counter.querySelector('.cart-count');
        if (cartCount) {
          cartCount.textContent = count;
        }
      });
    }

    /**
     * Toggle the empty state class based on line items
     */
    toggleEmptyCart() {
      this.currentItemCount = this.querySelectorAll('[data-line-item]').length;
      this.classList.toggle('cart-is-empty', this.currentItemCount === 0);
    }

    /**
     * Update live regions for accessibility
     * @param {string} line - The line item index
     * @param {string} message - The error message to display
     */
    updateLiveRegions(line, message) {
      const lineItemError = document.getElementById(`Line-item-error-${line}`);
      if (lineItemError) {
        lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
      }

      this.lineItemStatusElement.setAttribute('aria-hidden', true);

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }

    /**
     * Extract inner HTML from a section using provided selector
     * @param {string} html - The HTML string to parse
     * @param {string} selector - The selector to find within the HTML
     * @returns {string|undefined} The inner HTML of the matched element
     */
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    /**
     * Enable loading state
     */
    enableLoading() {
      document.querySelector('cart-items').classList.add('cart-items-is-loading');
      const loaderElements = document.querySelectorAll('[data-cart-loader-active="show"]');
      const contentElements = document.querySelectorAll('[data-cart-loader-active="hidden"]');

      loaderElements.forEach((el) => el.classList.remove('hidden'));
      contentElements.forEach((el) => el.classList.add('hidden'));

      document.activeElement.blur();
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }

    /**
     * Disable loading state
     */
    disableLoading() {
      const loaderElements = document.querySelectorAll('[data-cart-loader-active="show"]');
      const contentElements = document.querySelectorAll('[data-cart-loader-active="hidden"]');

      loaderElements.forEach((el) => el.classList.add('hidden'));
      contentElements.forEach((el) => el.classList.remove('hidden'));

      document.querySelector('cart-items').classList.remove('cart-items-is-loading');
    }
  }
  customElements.define('cart-items', CartItems);
}

/* Remove item from the cart  */
if (!customElements.get('cart-remove-button')) {
  class CartRemoveButton extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.closest('cart-items').updateQuantity(this.dataset.index, 0);
      });
    }
  }
  customElements.define('cart-remove-button', CartRemoveButton);
}

/* Update the cart note */
if (!customElements.get('cart-note')) {
  class CartNote extends HTMLElement {
    constructor() {
      super();
      this.addEventListener(
        'change',
        debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        }, 300)
      );
    }
  }
  customElements.define('cart-note', CartNote);
}
